import React, { useEffect, useState } from 'react'
import Layout from '../../brands/components/Layout'
import DataGrid from 'react-data-grid'
import { Icon } from '@iconify/react'
import Dropdown from 'react-bootstrap/Dropdown'
import { useTranslation } from 'react-i18next'
import SetLocation from '../components/SetLocation'
import { useNavigate } from 'react-router-dom'
import CustomPagination from '../../../components/Pagination'
import { Search } from '../../../components'
import { User } from '../type'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { HIDE_LOADER, SHOW_LOADER } from '../../../redux/slices/LoaderSlice'
import { getAllAdminUsers, resendEmail, toggleUserStatus } from '../api'
import { SHOW_NOTIFICATION } from '../../../redux/slices/showNotificationSlice'
import DeleteUser from '../components/DeleteUser/index'
import { setUsersList, deleteUsersList } from './slice'
import SelectField from '../../../components/Form/Select'
import './index.css'

const Users: React.FC = () => {
    const [users, setUsers] = useState<User[] | null>([])
    const { t } = useTranslation()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const usersState = useAppSelector((state) => state.users.userList)
    const { role } = useAppSelector((state) => state.persistedReducers.user)

    const [showLocModal, setShowLocModal] = useState<boolean>(false)
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [totalCount, setTotalCount] = useState<number>(0)
    const [userSearchKeyword, setUserSearchKeyword] = useState<string | null>(
        null
    )
    const [userId, setUserId] = useState<string>('')
    const [showUserDeleteModal, setShowUserDeleteModal] =
        useState<boolean>(false)
    const totalPages = Math.ceil(totalCount / 10)
    const [isUserInactive, setIsUserInactive] = useState<string>('Active')

    const toggleStatus = async (userid: string, active: boolean) => {
        try {
            const response = await toggleUserStatus(
                { userId: userid, activateUser: !active },
                userid
            )
            if (response.status === 200) {
                dispatch(
                    SHOW_NOTIFICATION({
                        message: `User ${active === true ? 'deactivated' : 'activated'} successfully`,
                        type: 'success',
                    })
                )
                dispatch(deleteUsersList(userid))
            }
        } catch (err) {
            // Handle any errors here
            dispatch(
                SHOW_NOTIFICATION({
                    message: 'Admin accounts cannot be deactivated',
                    type: 'error',
                })
            )
            console.error('Error fetching:', err)
        }
    }

    const handleUserDelete = async (userId: string) => {
        setUserId(userId)
        setShowUserDeleteModal(true)
    }

    const column = [
        {
            key: 'name',
            name: `${t('brand.tableColumns.name')}`,
            resizable: true,
            sortable: true,
            renderCell: (props: any) => {
                return (
                    <span style={{ color: '#39877C', fontWeight: '600' }}>
                        {props.row.name}
                    </span>
                )
            },
        },
        {
            key: 'location',
            name: `Location`,
            resizable: true,
            sortable: true,
        },
        {
            key: 'email',
            name: `Email`,
            resizable: true,
            sortable: true,
        },
        {
            key: 'phone',
            name: `Phone`,
            resizable: true,
            sortable: true,
        },
        {
            key: 'role',
            name: `Role`,
            resizable: true,
            sortable: true,
        },
        {
            key: 'actions',
            name: `${t('brand.tableColumns.action')}`, // Fix the typo here
            resizable: true,
            sortable: true,
            renderCell: (props: any) => {
                return (
                    <>
                        <div className="dropdown-custom-link">
                            <Icon
                                onClick={() => {
                                    navigate(`/profile?userId=${props.row.id}`)
                                }}
                                icon="bx:edit"
                                className="icon-primary"
                                cursor={'pointer'}
                            />
                            <Icon
                                onClick={() => handleUserDelete(props.row.id)}
                                icon="icon-park-outline:delete"
                                className="icon-secondary"
                                cursor={'pointer'}
                            />
                            <Dropdown>
                                <Dropdown.Toggle
                                    variant="success"
                                    id="dropdown-basic"
                                >
                                    <Icon icon="mage:dots" />
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        onClick={() =>
                                            navigate(
                                                `/users/user/change-password/${props.row.id}`
                                            )
                                        }
                                    >
                                        {t(
                                            'brand.tableColumns.actions.changePassword'
                                        )}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={async () => {
                                            dispatch(SHOW_LOADER())
                                            try {
                                                const response =
                                                    await resendEmail(
                                                        props.row.id
                                                    )
                                                dispatch(
                                                    SHOW_NOTIFICATION({
                                                        type: 'success',
                                                        message: response.data,
                                                    })
                                                )
                                                dispatch(HIDE_LOADER())
                                            } catch (err) {
                                                // Handle any errors here
                                                console.error(
                                                    'Error fetching:',
                                                    err
                                                )
                                            }
                                        }}
                                    >
                                        Resend Email
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => {
                                            toggleStatus(
                                                props.row.id,
                                                props.row.isActive
                                            )
                                        }}
                                    >
                                        {props.row.isActive === true
                                            ? t(
                                                  'brand.tableColumns.actions.deactivate'
                                              )
                                            : 'Activate'}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </>
                )
            },
        },
    ]

    useEffect(() => {
        dispatch(SHOW_LOADER())
        const fetchData = async () => {
            try {
                // Call the asynchronous function and wait for its result
                const userlisting = await getAllAdminUsers({
                    advancedSearch: {
                        keyword: userSearchKeyword,
                    },
                    isglobalSearch: false,
                    isActiveFilter: isUserInactive === 'Active' ? true : false,
                    currentPage: currentPage,
                    recordsPerPage: 10,
                })
                setUsers(userlisting?.data?.data)
                dispatch(setUsersList(userlisting?.data?.data))
                setTotalCount(userlisting?.data?.totalCount)
                dispatch(HIDE_LOADER())
            } catch (err) {
                // Handle any errors here
                console.error('Error fetching holidays:', err)
            }
        }

        // Call the function to fetch data
        fetchData()
    }, [currentPage, userSearchKeyword, isUserInactive])

    const getCurrentPage = (page: number) => {
        setCurrentPage(page)
    }
    useEffect(() => {
        setUsers(usersState)
    }, [usersState])

    return (
        <div>
            <SetLocation show={showLocModal} onClose={setShowLocModal} />
            <DeleteUser
                show={showUserDeleteModal}
                onClose={setShowUserDeleteModal}
                id={userId}
            />
            <Layout>
                <div className="col-12 grid-custom-table">
                    <div className="page-information mb-3">
                        <div className="row">
                            <div className="col-sm-6">
                                <h4>Manage Users</h4>
                            </div>
                            {role === 'Admin' && (
                                <div className="col-sm-6">
                                    <div className="custom-btn-design">
                                        <a
                                            onClick={() => {
                                                navigate('/users/add-new')
                                            }}
                                            className="button button-primary button-align-right"
                                        >
                                            ADD NEW USER
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-4">
                            <Search
                                placeholder="Search User"
                                onSearch={(e: any) => {
                                    setUserSearchKeyword(e)
                                }}
                            />
                        </div>
                        <div className="col-sm-3 inactive-select-filter">
                            <SelectField
                                name="userType"
                                label=""
                                options={[
                                    {
                                        value: 'Active',
                                        label: 'Active users',
                                    },
                                    {
                                        value: 'Inactive',
                                        label: 'Inactive users',
                                    },
                                ]}
                                value={isUserInactive}
                                onChange={(e) => {
                                    setIsUserInactive(e.target.value)
                                    setCurrentPage(1)
                                }}
                                onBlur={() => {
                                    return
                                }}
                                error={''}
                                touched={true}
                                defaultValue="option1"
                            />
                        </div>
                    </div>
                    {users && users?.length > 0 ? (
                        <div className="table-wrapper  grid-table-timeoff mt-4">
                            <DataGrid
                                className="data-grid-table"
                                columns={column}
                                // rowKeyGetter={(row: Brand) => row.id}
                                rows={users}
                                rowHeight={60}
                                headerRowHeight={40}
                                // onCellClick={(row) => {
                                //     setSelectedBrand(row.row)
                                // }}
                                // sortColumns={sortColumns}
                                // onSortColumnsChange={setSortColumns}
                            />
                        </div>
                    ) : (
                        <div className="no-records">No records found</div>
                    )}

                    {users && users?.length > 0 && (
                        <div className="mt-2">
                            <CustomPagination
                                totalPages={totalPages}
                                currentPage={currentPage}
                                getPageNumber={getCurrentPage}
                            />
                        </div>
                    )}
                </div>
            </Layout>
        </div>
    )
}

export default Users
